<template>
    <flex-element :element-type="elementType" class="layout" :class="collapsedClass">
        <aside :class="['layout__menu', editableClass]">
            <div class="layout__menu-primary">
                <button
                    class="layout__button"
                    type="button"
                    title="Select"
                    @click.stop="onSelectClick"
                >
                    <i class="layout__icon">
                        <font-awesome-icon :icon="['fal', 'bullseye']" />
                    </i>
                </button>

                <button
                    v-if="!isComponentScreen"
                    class="layout__button"
                    type="button"
                    title="Move"
                    @click.stop="onCut"
                >
                    <i class="layout__icon">
                        <font-awesome-icon :icon="['fal', 'arrows']" />
                    </i>
                </button>

                <button
                    class="layout__button"
                    type="button"
                    title="Column Wizard"
                    @click="onShow"
                >
                    <i class="layout__icon">
                        <font-awesome-icon :icon="['fal', 'list']" />
                    </i>
                </button>
            </div>

            <div class="layout__menu-secondary">
                <button
                    v-if="!isComponentScreen"
                    class="layout__button"
                    type="button"
                    title="Collapse"
                    @click="collapse"
                >
                    <i class="layout__icon">
                        <font-awesome-icon :icon="['fal', collapseIcon]" />
                    </i>
                </button>

                <button
                    v-if="!isComponentScreen"
                    class="layout__button"
                    type="button"
                    title="Remove"
                    @click.stop="removeRendering()"
                >
                    <i class="layout__icon">
                        <font-awesome-icon :icon="['fal', 'trash-alt']" />
                    </i>
                </button>
            </div>
        </aside>

        <div :class="['layout__container', editableClass]">
            <div v-if="mounted" class="layout__content">
                <drop-zone-multi
                    v-for="(columnClassString, i) in columnClassArray"
                    :key="i"
                    :column-classes="columnClassString"
                    :column-index="i"
                    :editable="editable"
                    :page-id="pageId"
                    :rendering-field="renderingField"
                    :top-level="topLevel"
                    display-name="Column"
                    is-layout-component
                    v-bind="element(i + 1)"
                    @element-updated="elementUpdated"
                    @class-updated="onColumnClassUpdate"
                />
            </div>
        </div>
    </flex-element>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import columnLayouts from '@/data/column-layouts';
import { reduceFraction } from '@/utils/math';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { STATE_CLASSES } from '@/data/constants/classes';
import { classDictionary } from '@/data/tailwind';
import { HIDE_DRAWER, SHOW_DRAWER } from '@/store/types/mutation-types';

export default {
    name: 'flex-layout',
    components: { FontAwesomeIcon },
    mixins: [baseOrganism, listenOnRootMixin],

    props: {
        displayName: {
            type: String,
            default: 'Layout',
        },

        numColumns: {
            type: Number,
            default: 2,
        },

        index: {
            type: Number,
            default: 0,
        },

        pageId: {
            type: String,
            default: '',
        },

        topLevel: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            columnClassArray: [],
            cssDisplay: 'block',
            guttersNew: null,
            isCollapsed: false,
            mounted: false,
            widthsNew: null,
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            selectedRendering: state => state.page.selectedRendering,
        }),

        collapseIcon() {
            return this.isCollapsed ? 'chevron-up' : 'chevron-down';
        },

        collapsedClass() {
            return {
                [STATE_CLASSES.COLLAPSED]: this.isCollapsed,
            };
        },

        defaultWidths() {
            let numColumnsNew = this.numColumns;

            if (this.widthsNew) return this.widthsNew;
            if (this.elementsNew.length) numColumnsNew = this.elementsNew.length;

            return {
                all: columnLayouts[numColumnsNew][0],
                lap: columnLayouts[numColumnsNew][0],
                tab: columnLayouts[numColumnsNew][0],
                mob: columnLayouts[numColumnsNew][0],
            };
        },

        defaultGutters() {
            if (this.guttersNew) return this.guttersNew;

            // in rems
            return {
                all: 0,
                lap: 0,
                tab: 0,
                mob: 0,
            };
        },

        editable() {
            return this.isTemplateScreen || (this.topLevel && this.renderingField !== 'shared');
        },

        editableClass() {
            if (this.editable) {
                return 'edit-mode';
            }
            return '';
        },
    },

    created() {
        this.atomicType = 'template';
        this.templateType = 'flex-layout';
    },

    mounted() {
        this.setLayout({
            widths: this.defaultWidths,
            gutters: this.defaultGutters,
        });

        this.listenOnRoot('flex::layout-drawer::save', this.onSaveLayout);

        // needed to delay the instantiation of <drop-zone-multi>
        // otherwise it will override any users-saved settings with the defaults
        this.mounted = true;
    },

    methods: {
        ...mapMutations({
            hideDrawer: HIDE_DRAWER,
            showDrawer: SHOW_DRAWER,
        }),

        collapse() {
            this.isCollapsed = !this.isCollapsed;
        },

        getFlexDirection() {
            return this.getElementData('flexDirection') || 'row';
        },

        onSelectClick() {
            this.setSelectedElement({ element: this });
        },

        onColumnClassUpdate(newColumnClasses, columnIndex) {
            // creating an entirely new array triggers the reactivity system
            // const newColumnClassArray = Array.from(this.columnClassArray);

            // newColumnClassArray[columnIndex] = newColumnClasses.join(' ');
            // this.columnClassArray = newColumnClassArray;

            newColumnClasses.forEach((className) => {
                const { tailwindDefinitionProp, key, screen } = classDictionary[className];
                const widths = [...this.widthsNew[screen]];

                switch (tailwindDefinitionProp) {
                    case 'width':
                        widths.splice(columnIndex, 1, key);
                        this.widthsNew[screen] = widths;
                        break;

                    case 'padding':
                        this.guttersNew[screen] = Number(key);
                        break;

                    default:
                        // do nothing
                }
            });
        },

        onSaveLayout({ id, widths, gutters, flexDirection }) {
            if (this.id === id) {
                this.setLayout({ widths, gutters, flexDirection });
            }
        },

        setLayout({ widths, gutters, flexDirection }) {
            this.setWidths(widths);
            this.setGutters(gutters);

            if (flexDirection) this.setFlexDirection(flexDirection);

            this.hideDrawer();
            this.$emit('element-updated', this.updatedElement);
        },

        setWidths(widths) {
            const {
                all: allWidths,
                lap: laptopWidths,
                tab: tabletWidths,
                mob: mobileWidths,
            } = widths;

            this.widthsNew = widths;

            if (allWidths.length < this.columnClassArray.length) {
                this.downsize(allWidths.length);
            }

            if (this.elementsNew.length === 0) {
                this.elementsNew = this.elementsNew.slice(0, allWidths.length);
            }

            this.columnClassArray = [];

            for (let i = 0, len = widths.all.length; i < len; i++) {
                const columnClassString = [
                    `f-w-${ reduceFraction(allWidths[i]) }`,
                    `lap:f-w-${ reduceFraction(laptopWidths[i]) }`,
                    `tab:f-w-${ reduceFraction(tabletWidths[i]) }`,
                    `mob:f-w-${ reduceFraction(mobileWidths[i]) }`,
                ].join(' ');

                this.columnClassArray[i] = columnClassString;
            }
        },

        // the gutters param is an array of numbers
        // each number represents the gutter (padding) in rems
        setGutters(gutters) {
            const {
                all: desktopGutter,
                lap: laptopGutter,
                tab: tabletGutter,
                mob: mobileGutter,
            } = gutters;

            for (let i = 0, len = this.columnClassArray.length; i < len; i++) {
                const gutterClassString = [
                    `f-p-${ desktopGutter }`,
                    `lap:f-p-${ laptopGutter }`,
                    `tab:f-p-${ tabletGutter }`,
                    `mob:f-p-${ mobileGutter }`,
                ].join(' ');

                this.columnClassArray[i] += ` ${ gutterClassString }`;
            }

            this.guttersNew = gutters;
        },

        setFlexDirection(flexDirection) {
            this.setElementData('flexDirection', flexDirection);
        },

        downsize(newLength) {
            const lastColumn = this.elementsNew[newLength - 1];
            let droppedOrganisms = [];

            // when downsizing, grab the organisms in the dropped columns
            // ... and add them to the last column of the new layout
            droppedOrganisms = this.elementsNew.slice(newLength).map(dropZone => dropZone.elements).flat();
            droppedOrganisms.forEach(org => lastColumn.elements.push(org));

            this.elementsNew.length = newLength;
        },

        onShow() {
            this.showDrawer({
                componentTag: 'layout-drawer',
                data: {
                    id: this.id,
                    defaultWidths: this.widthsNew,
                    defaultGutters: this.guttersNew,
                    defaultFlexDirection: this.getFlexDirection(),
                },
            });
        },

        onCut() {
            this.$emit('cut');
        },

        removeRendering() {
            this.$emit('delete-element', this.index);
        },

    },
};
</script>

<style lang="scss">
.layout {
    $this: &;

    &__container {
        position: relative;
        outline: none;

        .#{ $is-collapsed } & {
            overflow: hidden;
            max-height: 0;
        }

        &.edit-mode {
            border: solid 10px $layout-menu-color;

            .#{ $is-preview } & {
                border: none;
            }
        }
    }

    &__content {
        display: flex;
        flex-wrap: wrap;
    }

    &__menu {
        display: none;

        &.edit-mode {
            display: flex;
            padding: 0 30px 0 20px;

            .#{ $is-preview } & {
                display: none;
            }
        }
    }

    &__menu-primary {
        margin-right: auto;
    }

    &__menu-secondary {
        margin-left: auto;
    }

    &__button {
        background-color: $layout-menu-color;
        border: none;

        #{$this}__menu-primary & {
            margin-right: 4px;
        }

        #{$this}__menu-secondary & {
            margin-left: 4px;
        }
    }

    &__icon {
        color: $layout-icon-color;
        font-size: 14px;
        padding: 7px 12px;
        display: block;
    }
}
</style>
